import { useState, useEffect, useMemo } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Checkbox } from "./ui/checkbox";
import { Plus, Edit2, XCircle, RefreshCw, AlertTriangle, Calendar as CalendarIcon, List as ListIcon, Repeat, ChevronLeft, ChevronRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { authFetch, API } from "../utils/api";

/**
 * AdminBookingsManager — admin-only CRUD for bookings with NDIS-compliant
 * cancellation. Replaces the legacy read-only bookings table.
 *
 * Endpoints used:
 *  - GET    /api/admin/bookings          → list
 *  - POST   /api/admin/bookings          → create
 *  - PUT    /api/admin/bookings/{id}     → edit (date/time/duration/worker/status/notes)
 *  - POST   /api/admin/bookings/{id}/cancel → NDIS-aware cancel (short-notice billable flag)
 */
export function AdminBookingsManager() {
  const [bookings, setBookings] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // {mode:"create"|"edit"|"cancel", booking?, defaultDate?}
  const [statusFilter, setStatusFilter] = useState("all");
  const [view, setView] = useState("calendar");   // "calendar" | "list"
  const [selectedParticipantId, setSelectedParticipantId] = useState("");
  const [participantSearch, setParticipantSearch] = useState("");

  const load = async () => {
    setLoading(true);
    const qs = statusFilter === "all" ? "?limit=200" : `?limit=200&status=${statusFilter}`;
    const [bRes, uRes, wRes] = await Promise.all([
      authFetch(`${API}/admin/bookings${qs}`),
      authFetch(`${API}/admin/users?limit=500`),
      authFetch(`${API}/workers`),
    ]);
    if (bRes.ok) setBookings((await bRes.json()).bookings || []);
    if (uRes.ok) {
      const u = await uRes.json();
      const list = Array.isArray(u) ? u : (u.users || []);
      setParticipants(list.filter((x) => x.user_type === "participant"));
    }
    if (wRes.ok) {
      const w = await wRes.json();
      setWorkers(Array.isArray(w) ? w : (w.workers || []));
    }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const confirmBooking = async (booking) => {
    const r = await authFetch(`${API}/admin/bookings/${booking.booking_id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status: "confirmed" }),
    });
    if (r.ok) { toast.success("Shift confirmed"); load(); }
    else { const e = await r.json().catch(() => ({})); toast.error(e.detail || "Couldn't confirm"); }
  };

  const statusColor = (s) => ({
    pending: "bg-amber-100 text-amber-700",
    confirmed: "bg-emerald-100 text-emerald-700",
    completed: "bg-blue-100 text-blue-700",
    cancelled: "bg-rose-100 text-rose-700",
  }[s] || "bg-slate-100 text-slate-700");

  // ─── Per-participant filtering ─────────────────────────────────────
  const filteredParticipants = useMemo(() => {
    const q = participantSearch.trim().toLowerCase();
    if (!q) return participants;
    return participants.filter((p) =>
      (p.name || "").toLowerCase().includes(q) ||
      (p.email || "").toLowerCase().includes(q) ||
      (p.ndis_number || "").toLowerCase().includes(q)
    );
  }, [participants, participantSearch]);

  const selectedParticipant = useMemo(
    () => participants.find((p) => p.user_id === selectedParticipantId) || null,
    [participants, selectedParticipantId]
  );

  const visibleBookings = useMemo(
    () => selectedParticipantId ? bookings.filter((b) => b.user_id === selectedParticipantId) : bookings,
    [bookings, selectedParticipantId]
  );

  const openCreate = (defaultDate) => {
    setModal({
      mode: "create",
      defaultDate,
      defaultParticipantId: selectedParticipantId || undefined,
    });
  };

  return (
    <div className="space-y-4">
      {/* Participant selector — the whole tab is now scoped to one participant */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[220px]">
            <Label className="text-xs">Search participant</Label>
            <Input
              value={participantSearch}
              onChange={(e) => setParticipantSearch(e.target.value)}
              placeholder="Name, email or NDIS number…"
              className="mt-1"
              data-testid="admin-participant-search"
            />
          </div>
          <div className="flex-1 min-w-[220px]">
            <Label className="text-xs">Participant</Label>
            <select
              value={selectedParticipantId}
              onChange={(e) => setSelectedParticipantId(e.target.value)}
              className="mt-1 w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
              data-testid="admin-participant-select"
            >
              <option value="">All participants</option>
              {filteredParticipants.map((p) => (
                <option key={p.user_id} value={p.user_id}>
                  {p.name}{p.ndis_number ? ` · ${p.ndis_number}` : ""}
                </option>
              ))}
            </select>
          </div>
          <Button size="sm" className="bg-[#87CEEB] hover:bg-[#6BB8DB] text-white h-10 self-end" onClick={() => openCreate()} data-testid="admin-bookings-create">
            <Plus className="w-4 h-4 mr-1" />
            {selectedParticipant ? `Book for ${selectedParticipant.name.split(" ")[0]}` : "Create booking"}
          </Button>
        </div>

        {selectedParticipant && (
          <div className="mt-4 pt-4 border-t border-slate-100 flex flex-wrap items-center gap-4">
            <div>
              <div className="text-sm font-semibold text-slate-800">{selectedParticipant.name}</div>
              <div className="text-xs text-slate-500">
                {selectedParticipant.email}{selectedParticipant.phone ? ` · ${selectedParticipant.phone}` : ""}
              </div>
            </div>
            {selectedParticipant.ndis_number && (
              <div className="text-xs">
                <div className="text-slate-400 uppercase font-semibold">NDIS</div>
                <div className="text-slate-700 font-mono">{selectedParticipant.ndis_number}</div>
              </div>
            )}
            {selectedParticipant.ndis_plan_type && (
              <div className="text-xs">
                <div className="text-slate-400 uppercase font-semibold">Plan</div>
                <div className="text-slate-700">{String(selectedParticipant.ndis_plan_type).replace(/_/g, " ")}</div>
              </div>
            )}
            <div className="text-xs">
              <div className="text-slate-400 uppercase font-semibold">Shifts in view</div>
              <div className="text-slate-700 font-semibold">{visibleBookings.length}</div>
            </div>
            <div className="ml-auto flex gap-2">
              <Button size="sm" variant="outline" onClick={() => { setSelectedParticipantId(""); setParticipantSearch(""); }} data-testid="admin-participant-clear">
                <XCircle className="w-3.5 h-3.5 mr-1" /> Clear selection
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-semibold text-slate-900">
            {selectedParticipant ? `${selectedParticipant.name.split(" ")[0]}'s calendar` : "All bookings"}
          </h3>
          <p className="text-xs text-slate-500">
            {selectedParticipant
              ? "Add and manage shifts for this participant. Their name is auto-filled on every new booking."
              : "Pick a participant above to work with their calendar, or leave blank to see everyone."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-full bg-slate-100 p-1 text-xs font-semibold" data-testid="admin-bookings-view-toggle">
            <button
              type="button"
              onClick={() => setView("calendar")}
              className={`px-3 py-1.5 rounded-full transition-colors flex items-center gap-1 ${view === "calendar" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}
              data-testid="admin-view-calendar"
            >
              <CalendarIcon className="w-3.5 h-3.5" /> Calendar
            </button>
            <button
              type="button"
              onClick={() => setView("list")}
              className={`px-3 py-1.5 rounded-full transition-colors flex items-center gap-1 ${view === "list" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}
              data-testid="admin-view-list"
            >
              <ListIcon className="w-3.5 h-3.5" /> List
            </button>
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm"
            data-testid="admin-bookings-status-filter"
          >
            <option value="all">All statuses</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <Button size="sm" variant="outline" onClick={load} data-testid="admin-bookings-refresh">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {view === "calendar" ? (
        <BookingsCalendarView
          bookings={visibleBookings}
          loading={loading}
          statusColor={statusColor}
          onCreateOnDate={(iso) => openCreate(iso)}
          onEdit={(b) => setModal({ mode: "edit", booking: b })}
          onCancel={(b) => setModal({ mode: "cancel", booking: b })}
          onConfirm={confirmBooking}
        />
      ) : (
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className="text-left p-3 font-medium">Service</th>
                <th className="text-left p-3 font-medium">Participant</th>
                <th className="text-left p-3 font-medium">Worker</th>
                <th className="text-left p-3 font-medium">When</th>
                <th className="text-left p-3 font-medium">Amount</th>
                <th className="text-left p-3 font-medium">Status</th>
                <th className="text-left p-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="p-6 text-center text-slate-400">Loading…</td></tr>
              )}
              {!loading && visibleBookings.length === 0 && (
                <tr><td colSpan={7} className="p-6 text-center text-slate-400">No bookings yet.</td></tr>
              )}
              {visibleBookings.map((b) => (
                <tr key={b.booking_id} className="border-t" data-testid={`admin-booking-row-${b.booking_id}`}>
                  <td className="p-3 font-medium text-slate-900">{b.service_type}</td>
                  <td className="p-3 text-slate-600">{b.user_name || b.user_id}</td>
                  <td className="p-3 text-slate-600">{b.worker_name}</td>
                  <td className="p-3 text-slate-600">{b.date} {b.time} <span className="text-slate-400">({b.duration}h)</span></td>
                  <td className="p-3 text-slate-900 font-medium">${Number(b.total_cost || 0).toFixed(2)}</td>
                  <td className="p-3">
                    <Badge className={statusColor(b.status)}>{b.status}</Badge>
                    {b.cancellation?.short_notice && (
                      <span className="ml-2 inline-flex items-center text-[10px] font-semibold text-rose-600">
                        <AlertTriangle className="w-3 h-3 mr-0.5" /> Short notice · ${Number(b.cancellation.billable_amount || 0).toFixed(2)} billable
                      </span>
                    )}
                  </td>
                  <td className="p-3">
                    <div className="flex gap-1.5">
                      {b.status === "pending" && (
                        <Button size="sm" className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => confirmBooking(b)} data-testid={`admin-booking-confirm-${b.booking_id}`}>
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Confirm
                        </Button>
                      )}
                      <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setModal({ mode: "edit", booking: b })} data-testid={`admin-booking-edit-${b.booking_id}`}>
                        <Edit2 className="w-3.5 h-3.5 mr-1" /> Edit
                      </Button>
                      {b.status !== "cancelled" && (
                        <Button size="sm" variant="outline" className="h-8 text-xs text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => setModal({ mode: "cancel", booking: b })} data-testid={`admin-booking-cancel-${b.booking_id}`}>
                          <XCircle className="w-3.5 h-3.5 mr-1" /> Cancel
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </div>
      )}

      {modal?.mode === "create" && (
        <BookingFormModal
          mode="create"
          participants={participants}
          workers={workers}
          defaultDate={modal.defaultDate}
          defaultParticipantId={modal.defaultParticipantId}
          onClose={() => setModal(null)}
          onSaved={load}
        />
      )}
      {modal?.mode === "edit" && (
        <BookingFormModal mode="edit" booking={modal.booking} participants={participants} workers={workers} onClose={() => setModal(null)} onSaved={load} />
      )}
      {modal?.mode === "cancel" && (
        <CancelBookingModal booking={modal.booking} onClose={() => setModal(null)} onSaved={load} />
      )}
    </div>
  );
}


function BookingFormModal({ mode, booking, participants, workers, defaultDate, defaultParticipantId, onClose, onSaved }) {
  const [form, setForm] = useState({
    user_id: booking?.user_id || defaultParticipantId || "",
    worker_id: booking?.worker_id || "",
    service_type: booking?.service_type || "Personal Care",
    date: booking?.date || defaultDate || "",
    time: booking?.time || "09:00",
    duration: booking?.duration || 2,
    notes: booking?.notes || "",
    status: booking?.status || "confirmed",
  });
  const [saving, setSaving] = useState(false);
  const [repeatOn, setRepeatOn] = useState(false);
  const [repeatWeeks, setRepeatWeeks] = useState(4);
  const selectedWorker = workers.find((w) => w.worker_id === form.worker_id);
  const participantLocked = mode === "create" && !!defaultParticipantId;
  const previewCost = selectedWorker ? (selectedWorker.hourly_rate * form.duration).toFixed(2) : "—";

  // Compute repeat dates (weekly for N weeks starting on `form.date`)
  const repeatDates = useMemo(() => {
    if (!repeatOn || !form.date) return [];
    const out = [];
    const base = new Date(`${form.date}T00:00:00`);
    if (isNaN(base.getTime())) return [];
    for (let i = 0; i < Number(repeatWeeks || 0); i++) {
      const d = new Date(base);
      d.setDate(d.getDate() + i * 7);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      out.push(`${y}-${m}-${day}`);
    }
    return out;
  }, [repeatOn, form.date, repeatWeeks]);

  const submit = async () => {
    if (!form.user_id || !form.worker_id || !form.date || !form.time || !form.duration) {
      toast.error("Please fill participant, worker, date, time and duration");
      return;
    }
    setSaving(true);
    try {
      // Bulk-create path when Repeat is enabled on a new booking
      if (mode === "create" && repeatOn && repeatDates.length > 1) {
        const r = await authFetch(`${API}/admin/bookings/bulk`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_id: form.user_id,
            worker_id: form.worker_id,
            service_type: form.service_type,
            dates: repeatDates,
            time: form.time,
            duration: form.duration,
            notes: form.notes,
            status: form.status,
          }),
        });
        if (r.ok) {
          const d = await r.json();
          if (d.skipped_count) {
            toast.success(`${d.created_count} shift${d.created_count === 1 ? "" : "s"} created — ${d.skipped_count} skipped (clashes)`);
          } else {
            toast.success(`${d.created_count} shifts created`);
          }
          onSaved?.(); onClose?.();
          return;
        }
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || "Bulk create failed");
        return;
      }

      const url = mode === "create"
        ? `${API}/admin/bookings`
        : `${API}/admin/bookings/${booking.booking_id}`;
      const r = await authFetch(url, {
        method: mode === "create" ? "POST" : "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (r.ok) {
        toast.success(mode === "create" ? "Booking created" : "Booking updated");
        onSaved?.(); onClose?.();
      } else {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || "Save failed");
      }
    } finally { setSaving(false); }
  };

  return (
    <ModalShell title={mode === "create" ? "Create booking" : "Edit booking"} onClose={onClose}>
      <div className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label>Participant{participantLocked && <span className="ml-1 text-[10px] text-emerald-600 font-normal">(pre-selected)</span>}</Label>
            <select value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })}
              disabled={participantLocked}
              className={`mt-1 w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm ${participantLocked ? "bg-slate-50 cursor-not-allowed" : ""}`}
              data-testid="modal-booking-participant">
              <option value="">— select —</option>
              {participants.map((p) => (
                <option key={p.user_id} value={p.user_id}>{p.name} ({p.email})</option>
              ))}
            </select>
          </div>
          <div>
            <Label>Worker</Label>
            <select value={form.worker_id} onChange={(e) => setForm({ ...form, worker_id: e.target.value })}
              className="mt-1 w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
              data-testid="modal-booking-worker">
              <option value="">— select —</option>
              {workers.map((w) => (
                <option key={w.worker_id} value={w.worker_id}>{w.name} · ${w.hourly_rate}/hr</option>
              ))}
            </select>
          </div>
          <div>
            <Label>Service</Label>
            <Input value={form.service_type} onChange={(e) => setForm({ ...form, service_type: e.target.value })}
              data-testid="modal-booking-service" />
          </div>
          <div>
            <Label>Status</Label>
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
              className="mt-1 w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
              data-testid="modal-booking-status">
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="completed">Completed</option>
            </select>
          </div>
          <div>
            <Label>Date</Label>
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
              data-testid="modal-booking-date" />
          </div>
          <div>
            <Label>Time</Label>
            <Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })}
              data-testid="modal-booking-time" />
          </div>
          <div>
            <Label>Duration (hours)</Label>
            <Input type="number" min="1" max="24" value={form.duration}
              onChange={(e) => setForm({ ...form, duration: Number(e.target.value) })}
              data-testid="modal-booking-duration" />
          </div>
          <div className="flex items-end">
            <div className="bg-slate-50 rounded-md px-3 py-2 w-full text-sm">
              <span className="text-slate-500">Estimated cost:</span>
              <span className="ml-2 font-semibold text-slate-900">${previewCost}</span>
            </div>
          </div>
        </div>
        <div>
          <Label>Notes (visible to worker)</Label>
          <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
            data-testid="modal-booking-notes" placeholder="Address, access instructions, anything the worker should know…" />
        </div>

        {mode === "create" && (
          <div className="rounded-xl border border-slate-200 p-3 bg-slate-50/60" data-testid="repeat-block">
            <div className="flex items-start gap-2">
              <Checkbox
                id="repeat-on"
                checked={repeatOn}
                onCheckedChange={(c) => setRepeatOn(!!c)}
                data-testid="repeat-checkbox"
              />
              <label htmlFor="repeat-on" className="text-sm text-slate-700 cursor-pointer flex items-center gap-1.5">
                <Repeat className="w-3.5 h-3.5 text-[#5BA3C0]" /> Repeat weekly (same day + time)
              </label>
            </div>
            {repeatOn && (
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                  <Label className="text-xs whitespace-nowrap">Number of weeks</Label>
                  <Input
                    type="number"
                    min="1"
                    max="52"
                    value={repeatWeeks}
                    onChange={(e) => setRepeatWeeks(Math.max(1, Number(e.target.value || 1)))}
                    className="h-9 w-24"
                    data-testid="repeat-weeks"
                  />
                </div>
                <p className="text-xs text-slate-500 flex-1 min-w-0">
                  Creates <strong>{repeatDates.length}</strong> shifts from{" "}
                  <strong>{repeatDates[0] || "—"}</strong> through <strong>{repeatDates[repeatDates.length - 1] || "—"}</strong>.
                  Clashes with existing bookings are skipped automatically.
                </p>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#87CEEB] hover:bg-[#6BB8DB] text-white" data-testid="modal-booking-save">
            {saving ? "Saving…" : mode === "create" ? (repeatOn && repeatDates.length > 1 ? `Create ${repeatDates.length} shifts` : "Create") : "Save changes"}
          </Button>
        </div>
      </div>
    </ModalShell>
  );
}


function CancelBookingModal({ booking, onClose, onSaved }) {
  const [cancelledBy, setCancelledBy] = useState("participant");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState(null); // local computed preview

  // Local preview of NDIS rule before submitting (server is source of truth).
  useEffect(() => {
    try {
      if (!booking?.date || !booking?.time) return;
      const start = new Date(`${booking.date}T${booking.time}`);
      const hours = (start.getTime() - Date.now()) / 3_600_000;
      const fee = Number(booking.total_cost || 0);
      if (cancelledBy === "no_show") {
        setPreview({ billable: true, msg: `Participant no-show — provider may claim 100% ($${fee.toFixed(2)}).` });
      } else if (cancelledBy === "worker" || cancelledBy === "admin") {
        setPreview({ billable: false, msg: `Cancelled by ${cancelledBy} — no charge to the participant.` });
      } else if (hours < 7 * 24) {
        setPreview({ billable: true, msg: `Short notice — ${(hours / 24).toFixed(1)} day(s) before shift. Provider may claim 100% ($${fee.toFixed(2)}).` });
      } else {
        setPreview({ billable: false, msg: `${(hours / 24).toFixed(1)} day(s) notice — beyond the 7-day NDIS short-notice window. No charge.` });
      }
    } catch (_) { setPreview(null); }
  }, [cancelledBy, booking]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await authFetch(`${API}/admin/bookings/${booking.booking_id}/cancel`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cancelled_by: cancelledBy, reason }),
      });
      if (r.ok) {
        const d = await r.json();
        toast.success(d.cancellation?.policy_reason || "Booking cancelled");
        onSaved?.(); onClose?.();
      } else {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || "Cancel failed");
      }
    } finally { setSubmitting(false); }
  };

  return (
    <ModalShell title="Cancel booking" onClose={onClose}>
      <div className="space-y-3">
        <div className="bg-slate-50 rounded-lg p-3 text-sm">
          <p className="font-medium text-slate-900">{booking.service_type} · {booking.worker_name}</p>
          <p className="text-slate-500 text-xs">{booking.date} {booking.time} · {booking.duration}h · ${Number(booking.total_cost || 0).toFixed(2)}</p>
        </div>
        <div>
          <Label>Cancelled by</Label>
          <select value={cancelledBy} onChange={(e) => setCancelledBy(e.target.value)}
            className="mt-1 w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
            data-testid="modal-cancel-by">
            <option value="participant">Participant</option>
            <option value="worker">Worker</option>
            <option value="admin">Admin / BTR Health</option>
            <option value="no_show">No-show (participant didn&apos;t attend)</option>
          </select>
        </div>
        <div>
          <Label>Reason (audit trail)</Label>
          <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
            data-testid="modal-cancel-reason" placeholder="Hospital admission, family emergency, etc." />
        </div>
        {preview && (
          <div className={`rounded-lg p-3 text-xs border ${preview.billable ? "bg-rose-50 border-rose-200 text-rose-700" : "bg-emerald-50 border-emerald-200 text-emerald-700"}`} data-testid="modal-cancel-preview">
            <p className="font-bold mb-0.5">{preview.billable ? "Billable" : "Not billable"}</p>
            <p>{preview.msg}</p>
            <p className="mt-1.5 text-[10px] opacity-80">Per NDIS Pricing Arrangements 2026-27, Part B §8 — short notice = within 7 clear days of the scheduled start time.</p>
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Keep booking</Button>
          <Button onClick={submit} disabled={submitting} className="bg-rose-500 hover:bg-rose-600 text-white" data-testid="modal-cancel-confirm">
            {submitting ? "Cancelling…" : "Confirm cancellation"}
          </Button>
        </div>
      </div>
    </ModalShell>
  );
}


function ModalShell({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b px-5 py-3 flex items-center justify-between">
          <h4 className="font-semibold text-slate-900">{title}</h4>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">✕</button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}


// ─── Calendar view ─────────────────────────────────────────────────────────
const DAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS_LONG = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function toISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function BookingsCalendarView({ bookings, loading, statusColor, onCreateOnDate, onEdit, onCancel, onConfirm }) {
  const [cursor, setCursor] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [scale, setScale] = useState("month");   // "month" | "week" | "day"

  const byDate = useMemo(() => {
    const map = {};
    for (const b of bookings) {
      const key = b.date;
      if (!key) continue;
      (map[key] = map[key] || []).push(b);
    }
    Object.keys(map).forEach((k) => map[k].sort((a, b) => (a.time || "").localeCompare(b.time || "")));
    return map;
  }, [bookings]);

  const today = new Date();
  const isToday = (d) =>
    d.getDate() === today.getDate() &&
    d.getMonth() === today.getMonth() &&
    d.getFullYear() === today.getFullYear();

  const shiftCursor = (dir) => {
    const n = new Date(cursor);
    if (scale === "month") n.setMonth(n.getMonth() + dir);
    else if (scale === "week") n.setDate(n.getDate() + 7 * dir);
    else n.setDate(n.getDate() + dir);
    setCursor(n);
  };

  const rangeLabel = useMemo(() => {
    if (scale === "month") return `${MONTHS_LONG[cursor.getMonth()]} ${cursor.getFullYear()}`;
    if (scale === "day") {
      return cursor.toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    }
    // week
    const start = startOfWeek(cursor);
    const end = new Date(start); end.setDate(end.getDate() + 6);
    const sameMonth = start.getMonth() === end.getMonth();
    if (sameMonth) return `${start.getDate()} – ${end.getDate()} ${MONTHS_LONG[end.getMonth()]} ${end.getFullYear()}`;
    return `${start.getDate()} ${MONTHS_LONG[start.getMonth()].slice(0,3)} – ${end.getDate()} ${MONTHS_LONG[end.getMonth()].slice(0,3)} ${end.getFullYear()}`;
  }, [scale, cursor]);

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden" data-testid="admin-bookings-calendar">
        <div className="p-3 flex flex-wrap items-center justify-between gap-2 border-b border-slate-100">
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => shiftCursor(-1)}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              data-testid="calendar-prev"
              aria-label="Previous"
            >
              <ChevronLeft className="w-5 h-5 text-slate-600" />
            </button>
            <button
              type="button"
              onClick={() => setCursor(new Date())}
              className="px-3 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-100 rounded-lg border border-slate-200"
              data-testid="calendar-today"
            >
              Today
            </button>
            <button
              type="button"
              onClick={() => shiftCursor(1)}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              data-testid="calendar-next"
              aria-label="Next"
            >
              <ChevronRight className="w-5 h-5 text-slate-600" />
            </button>
          </div>
          <h3 className="text-base font-semibold text-slate-800 flex-1 text-center min-w-0 truncate" data-testid="calendar-range-label">
            {rangeLabel}
          </h3>
          <div className="inline-flex rounded-full bg-slate-100 p-1 text-xs font-semibold">
            {["month","week","day"].map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setScale(s)}
                className={`px-3 py-1.5 rounded-full transition-colors capitalize ${scale === s ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}
                data-testid={`calendar-scale-${s}`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {scale === "month" && (
          <MonthGrid cursor={cursor} byDate={byDate} statusColor={statusColor} onSelectDate={setSelectedDate} selectedIso={selectedDate ? toISO(selectedDate) : null} isToday={isToday} onCreateOnDate={onCreateOnDate} onEdit={onEdit} />
        )}
        {scale === "week" && (
          <TimeGrid days={7} cursor={cursor} byDate={byDate} statusColor={statusColor} isToday={isToday} onCreateOnDate={onCreateOnDate} onEdit={onEdit} />
        )}
        {scale === "day" && (
          <TimeGrid days={1} cursor={cursor} byDate={byDate} statusColor={statusColor} isToday={isToday} onCreateOnDate={onCreateOnDate} onEdit={onEdit} />
        )}

        <div className="px-4 py-3 flex items-center justify-between text-xs text-slate-500 border-t border-slate-100 bg-slate-50/50">
          <span>
            {scale === "month" ? "Click a day for details · Double-click or use + to add" : "Click a shift to edit · Click any empty slot to add"}
          </span>
          {loading && <span className="text-slate-400">Loading…</span>}
        </div>
      </div>

      {/* Selected day panel (Month view only) */}
      {scale === "month" && selectedDate && (
        <SelectedDayPanel
          date={selectedDate}
          list={byDate[toISO(selectedDate)] || []}
          statusColor={statusColor}
          onCreate={() => onCreateOnDate(toISO(selectedDate))}
          onEdit={onEdit}
          onCancel={onCancel}
          onConfirm={onConfirm}
        />
      )}
    </div>
  );
}

function startOfWeek(d) {
  const s = new Date(d);
  s.setHours(0,0,0,0);
  s.setDate(s.getDate() - s.getDay());  // week starts Sunday
  return s;
}

function MonthGrid({ cursor, byDate, statusColor, onSelectDate, selectedIso, isToday, onCreateOnDate, onEdit }) {
  const days = useMemo(() => {
    const y = cursor.getFullYear();
    const m = cursor.getMonth();
    const firstDay = new Date(y, m, 1);
    const lastDay = new Date(y, m + 1, 0);
    const startingDay = firstDay.getDay();
    const daysInMonth = lastDay.getDate();
    const arr = [];
    const prevMonthLast = new Date(y, m, 0).getDate();
    for (let i = startingDay - 1; i >= 0; i--) {
      arr.push({ date: prevMonthLast - i, inMonth: false, full: new Date(y, m - 1, prevMonthLast - i) });
    }
    for (let d = 1; d <= daysInMonth; d++) {
      arr.push({ date: d, inMonth: true, full: new Date(y, m, d) });
    }
    const remaining = 42 - arr.length;
    for (let d = 1; d <= remaining; d++) {
      arr.push({ date: d, inMonth: false, full: new Date(y, m + 1, d) });
    }
    return arr;
  }, [cursor]);

  return (
    <>
      <div className="grid grid-cols-7 border-b border-slate-100">
        {DAYS_SHORT.map((d) => (
          <div key={d} className="py-2 text-center text-xs font-medium text-slate-500">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((day, idx) => {
          const iso = toISO(day.full);
          const dayBookings = day.inMonth ? (byDate[iso] || []) : [];
          const isTodayCell = isToday(day.full);
          const isSelected = selectedIso === iso;
          return (
            <div
              key={idx}
              className={`min-h-[92px] p-1.5 border-b border-r border-slate-100 text-left relative transition-colors ${
                !day.inMonth
                  ? "bg-slate-50/60"
                  : isSelected
                    ? "bg-[#87CEEB]/15"
                    : "hover:bg-[#87CEEB]/5 cursor-pointer"
              }`}
              onClick={() => day.inMonth && onSelectDate(day.full)}
              onDoubleClick={() => day.inMonth && onCreateOnDate(iso)}
              data-testid={day.inMonth ? `admin-cal-day-${iso}` : undefined}
            >
              <div className="flex items-center justify-between mb-1">
                <span
                  className={`text-xs font-semibold ${
                    isTodayCell
                      ? "w-6 h-6 rounded-full bg-[#87CEEB] text-white flex items-center justify-center"
                      : day.inMonth ? "text-slate-700 pl-1" : "text-slate-300 pl-1"
                  }`}
                >
                  {day.date}
                </span>
                {day.inMonth && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onCreateOnDate(iso); }}
                    className="opacity-0 hover:opacity-100 group-hover:opacity-100 text-slate-400 hover:text-[#5BA3C0] p-0.5"
                    title="Add shift"
                    aria-label="Add shift"
                  >
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <div className="space-y-1 overflow-hidden">
                {dayBookings.slice(0, 2).map((b) => (
                  <button
                    key={b.booking_id}
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onEdit(b); }}
                    className={`w-full text-left text-[10px] leading-tight rounded px-1 py-0.5 ${statusColor(b.status)} truncate`}
                    title={`${b.time} ${b.service_type} · ${b.worker_name}`}
                  >
                    {b.time} · {b.user_name?.split(" ")[0]}
                  </button>
                ))}
                {dayBookings.length > 2 && (
                  <div className="text-[10px] text-slate-500 font-semibold">+{dayBookings.length - 2} more</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

/**
 * Time-gridded view like Google Calendar's Week/Day. Hours 6am–10pm on the
 * left, day columns across. Each shift is an absolute-positioned coloured
 * block sized by duration.
 */
function TimeGrid({ days, cursor, byDate, statusColor, isToday, onCreateOnDate, onEdit }) {
  const HOUR_START = 6;
  const HOUR_END = 22;   // exclusive
  const HOUR_ROWS = HOUR_END - HOUR_START;   // 16 rows
  const ROW_PX = 44;

  const columns = useMemo(() => {
    if (days === 1) return [new Date(cursor)];
    const s = startOfWeek(cursor);
    return Array.from({ length: days }, (_, i) => { const d = new Date(s); d.setDate(d.getDate() + i); return d; });
  }, [days, cursor]);

  const parseTime = (t) => {
    if (!t) return [9, 0];
    const [h, m] = t.split(":").map(Number);
    return [isNaN(h) ? 9 : h, isNaN(m) ? 0 : m];
  };

  return (
    <div className="overflow-auto" style={{ maxHeight: 620 }}>
      <div className="min-w-[720px]">
        {/* Day headers */}
        <div className="grid sticky top-0 bg-white z-10 border-b border-slate-100" style={{ gridTemplateColumns: `56px repeat(${days}, 1fr)` }}>
          <div />
          {columns.map((d, i) => (
            <div key={i} className="py-2 text-center text-xs">
              <div className="text-slate-500 font-medium">{DAYS_SHORT[d.getDay()]}</div>
              <div className={`mt-0.5 mx-auto ${isToday(d) ? "w-7 h-7 rounded-full bg-[#87CEEB] text-white flex items-center justify-center font-bold" : "font-semibold text-slate-700"}`}>
                {d.getDate()}
              </div>
            </div>
          ))}
        </div>

        {/* Time grid */}
        <div className="grid" style={{ gridTemplateColumns: `56px repeat(${days}, 1fr)`, position: "relative" }}>
          {/* Hour labels column */}
          <div className="border-r border-slate-100">
            {Array.from({ length: HOUR_ROWS }, (_, i) => (
              <div key={i} className="h-11 flex items-start justify-end pr-2 pt-0.5 text-[10px] text-slate-400">
                {formatHour(HOUR_START + i)}
              </div>
            ))}
          </div>

          {/* Day columns */}
          {columns.map((d, i) => {
            const iso = toISO(d);
            const shifts = byDate[iso] || [];
            return (
              <div key={i} className="border-r border-slate-100 relative">
                {/* Hour cells */}
                {Array.from({ length: HOUR_ROWS }, (_, h) => (
                  <div
                    key={h}
                    className="h-11 border-b border-slate-100 hover:bg-[#87CEEB]/5 cursor-pointer"
                    onClick={() => onCreateOnDate(iso)}
                    data-testid={`admin-week-slot-${iso}-${HOUR_START + h}`}
                    title="Click to add a shift on this day"
                  />
                ))}
                {/* Shift blocks */}
                {shifts.map((b) => {
                  const [sh, sm] = parseTime(b.time);
                  const startMin = sh * 60 + sm - HOUR_START * 60;
                  const totalMin = (b.duration || 1) * 60;
                  if (startMin < 0 || startMin >= HOUR_ROWS * 60) return null;
                  const top = (startMin / 60) * ROW_PX;
                  const height = Math.max(20, (totalMin / 60) * ROW_PX - 2);
                  return (
                    <button
                      key={b.booking_id}
                      type="button"
                      onClick={() => onEdit(b)}
                      className={`absolute left-1 right-1 rounded-md px-2 py-1 text-left border ${statusColor(b.status)} border-white/60 shadow-sm overflow-hidden`}
                      style={{ top, height }}
                      title={`${b.time} · ${b.service_type} · ${b.worker_name} → ${b.user_name}`}
                      data-testid={`admin-week-shift-${b.booking_id}`}
                    >
                      <div className="text-[10px] font-bold leading-tight truncate">
                        {b.time} · {b.duration}h
                      </div>
                      <div className="text-[10px] truncate">{b.user_name?.split(" ")[0]} · {b.service_type}</div>
                      {height > 60 && (
                        <div className="text-[9px] truncate opacity-75">w/ {b.worker_name?.split(" ")[0]}</div>
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function formatHour(h) {
  if (h === 0) return "12 AM";
  if (h === 12) return "12 PM";
  return h < 12 ? `${h} AM` : `${h - 12} PM`;
}

function SelectedDayPanel({ date, list, statusColor, onCreate, onEdit, onCancel, onConfirm }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4" data-testid="admin-cal-day-detail">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-slate-800">
          {date.toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
          <span className="ml-2 text-xs font-normal text-slate-500">
            {list.length ? `${list.length} shift${list.length === 1 ? "" : "s"}` : "no shifts yet"}
          </span>
        </h4>
        <Button size="sm" onClick={onCreate} className="bg-[#87CEEB] hover:bg-[#6BB8DB] text-white" data-testid="admin-cal-add-on-selected">
          <Plus className="w-4 h-4 mr-1" /> Add shift on this day
        </Button>
      </div>
      <div className="space-y-2">
        {list.length === 0 && (
          <p className="text-xs text-slate-400 py-4 text-center">
            No shifts on this day. Click &quot;Add shift&quot; above to book one.
          </p>
        )}
        {list.map((b) => (
          <div key={b.booking_id} className="flex items-center justify-between border border-slate-100 rounded-xl p-3" data-testid={`admin-cal-shift-${b.booking_id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-slate-800 text-sm">{b.time}</span>
                <span className="text-slate-500 text-xs">·</span>
                <span className="text-sm text-slate-700">{b.service_type}</span>
                <Badge className={statusColor(b.status)}>{b.status}</Badge>
              </div>
              <div className="text-xs text-slate-500 mt-1">
                {b.user_name} → {b.worker_name} · {b.duration}h · ${Number(b.total_cost || 0).toFixed(2)}
              </div>
            </div>
            <div className="flex gap-1.5 ml-3 flex-shrink-0">
              {b.status === "pending" && onConfirm && (
                <Button size="sm" className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => onConfirm(b)} data-testid={`admin-cal-confirm-${b.booking_id}`}>
                  <CheckCircle2 className="w-3.5 h-3.5" />
                </Button>
              )}
              <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => onEdit(b)}>
                <Edit2 className="w-3.5 h-3.5" />
              </Button>
              {b.status !== "cancelled" && (
                <Button size="sm" variant="outline" className="h-8 text-xs text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => onCancel(b)}>
                  <XCircle className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
